import { useMutation } from "@tanstack/react-query";
import { createAgency } from "../../../data";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Building2,
  Mail,
  Phone,
  CreditCard,
  MapPin,
  Briefcase,
  User,
  Percent,
  FileText,
} from "lucide-react";

export function AgencyAddScreen() {
  const pageSettings = {
    title: "Ajans Oluştur",
    actions: [
      {
        name: "Listeye Dön",
        color: "blue",
        icon: "add-icon",
        onClick: () => {
          navigate("/agencies");
        },
      },
    ],
  };

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const mutation = useMutation({
    mutationKey: ["createAgency"],
    mutationFn: (data) => {
      return createAgency(data);
    },
    onSuccess: (response) => {
      navigate("/agencies");
    },
  });

  const onSubmit = async (data) => {
    mutation.mutate({
      email: data.email?.trim(),
      name: data.name?.trim(),
      phoneNumber: data.phoneNumber?.trim(),
      billingName: data.billingName?.trim(),
      bankName: data.bankName?.trim(),
      IBAN: data.IBAN?.trim(),
      SWIFT: data.SWIFT?.trim(),
      country: data.country?.trim(),
      currency: data.currency?.trim(),
      lucaId: data.lucaId?.trim(),
      lucaAccountName: data.lucaAccountName?.trim(),
      newTvAccountManager: data.newTvAccountManager?.trim(),
      agencyCommissionPercentage: data.agencyCommissionPercentage ? parseFloat(data.agencyCommissionPercentage) : null,
      isPrepareTheInvoiceCorporate: data.isPrepareTheInvoiceCorporate || false,
    });
  };

  return (
    <AppLayout title={pageSettings.title} actions={pageSettings.actions}>
      <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5 text-orange-600" />
                  Temel Bilgiler
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Agency Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Ajans adını girin"
                    className="mt-1.5"
                    {...register("name", {
                      required: "Ad zorunludur",
                    })}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="agency@example.com"
                    className="mt-1.5"
                    {...register("email", {
                      required: "E-posta zorunludur",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Geçersiz e-posta formatı",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phoneNumber" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="text"
                    placeholder="+1 234 567 8900"
                    className="mt-1.5"
                    {...register("phoneNumber", {
                      required: "Telefon numarası zorunludur",
                    })}
                  />
                  {errors.phoneNumber && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="billingName" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Billing Name *
                  </Label>
                  <Input
                    id="billingName"
                    type="text"
                    placeholder="Fatura adı"
                    className="mt-1.5"
                    {...register("billingName", {
                      required: "Fatura adı zorunludur",
                    })}
                  />
                  {errors.billingName && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.billingName.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Location & Manager */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  Konum ve Yönetim
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="country" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Ülke
                  </Label>
                  <Input
                    id="country"
                    type="text"
                    placeholder="Ülke"
                    className="mt-1.5"
                    {...register("country")}
                  />
                </div>

                <div>
                  <Label htmlFor="currency" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Para Birimi
                  </Label>
                  <Input
                    id="currency"
                    type="text"
                    placeholder="TRY"
                    defaultValue="TRY"
                    className="mt-1.5"
                    {...register("currency")}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="newTvAccountManager"
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    NewTV Hesap Yöneticisi
                  </Label>
                  <Input
                    id="newTvAccountManager"
                    type="text"
                    placeholder="Menajer adı"
                    className="mt-1.5"
                    {...register("newTvAccountManager")}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Luca Account */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="h-5 w-5 text-emerald-600" />
                  Luca Hesabı
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="lucaId" className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Luca ID
                  </Label>
                  <Input
                    id="lucaId"
                    type="text"
                    placeholder="Luca ID"
                    className="mt-1.5 font-mono"
                    {...register("lucaId")}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="lucaAccountName"
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    Luca Hesap Adı
                  </Label>
                  <Input
                    id="lucaAccountName"
                    type="text"
                    placeholder="Luca Hesap Adı"
                    className="mt-1.5"
                    {...register("lucaAccountName")}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Commission Settings */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Percent className="h-5 w-5 text-green-600" />
                  Komisyon Ayarları
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label
                    htmlFor="agencyCommissionPercentage"
                    className="flex items-center gap-2"
                  >
                    <Percent className="h-4 w-4" />
                    Ajans Komisyon Yüzdesi (%)
                  </Label>
                  <Input
                    id="agencyCommissionPercentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="e.g. 10"
                    className="mt-1.5"
                    {...register("agencyCommissionPercentage")}
                  />
                  {errors.agencyCommissionPercentage && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.agencyCommissionPercentage.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isPrepareTheInvoiceCorporate"
                    name="isPrepareTheInvoiceCorporate"
                    onCheckedChange={(checked) => {
                      setValue("isPrepareTheInvoiceCorporate", checked);
                    }}
                    {...register("isPrepareTheInvoiceCorporate")}
                  />
                  <Label
                    htmlFor="isPrepareTheInvoiceCorporate"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Kurumsal Fatura Kesilsin
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Banking Details */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5 text-cyan-600" />
                  Banka Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="bankName" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Bank Name *
                  </Label>
                  <Input
                    id="bankName"
                    type="text"
                    placeholder="Banka adı"
                    className="mt-1.5"
                    {...register("bankName", {
                      required: "Banka adı zorunludur",
                    })}
                  />
                  {errors.bankName && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.bankName.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="IBAN" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    IBAN *
                  </Label>
                  <Input
                    id="IBAN"
                    type="text"
                    placeholder="IBAN"
                    className="mt-1.5 font-mono"
                    {...register("IBAN", {
                      required: "IBAN zorunludur",
                    })}
                  />
                  {errors.IBAN && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.IBAN.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="SWIFT" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    SWIFT *
                  </Label>
                  <Input
                    id="SWIFT"
                    type="text"
                    placeholder="SWIFT/BIC kodu"
                    className="mt-1.5 font-mono"
                    {...register("SWIFT", {
                      required: "SWIFT zorunludur",
                    })}
                  />
                  {errors.SWIFT && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.SWIFT.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/agencies")}
          >
            İptal
          </Button>
          <Button
            type="submit"
            className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
          >
            Ajans Oluştur
          </Button>
        </div>
      </form>
    </AppLayout>
  );
}
